/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Plus } from 'lucide-react';
import { type ChangeEvent, type SyntheticEvent, useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type TherapyType } from '../../lib/api/therapy-types.js';
import {
  useCreateTherapyType,
  useTherapyTypes,
  useUpdateTherapyType,
} from '../../lib/queries/therapy-types.js';

const INPUT =
  'focus-ring rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';

function TypeRow({ item }: { readonly item: TherapyType }): JSX.Element {
  const { t } = useTranslation();
  const update = useUpdateTherapyType();
  const toggle = useCallback(() => {
    update.mutate({ id: item.id, payload: { active: !item.active } });
  }, [update, item.id, item.active]);
  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/60">
      <td className="px-5 py-3 font-medium text-slate-800 dark:text-white">{item.name}</td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
        {t('settings.therapyTypes.mins', { count: item.defaultDurationMins })}
      </td>
      <td className="px-5 py-3">
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${item.active ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10' : 'bg-slate-100 text-slate-500 dark:bg-slate-800'}`}
        >
          {item.active ? t('settings.therapyTypes.active') : t('settings.therapyTypes.inactive')}
        </span>
      </td>
      <td className="px-5 py-3 text-right">
        <Button variant="ghost" size="sm" onClick={toggle} disabled={update.isPending}>
          {item.active
            ? t('settings.therapyTypes.deactivate')
            : t('settings.therapyTypes.activate')}
        </Button>
      </td>
    </tr>
  );
}

function AddTypeForm(): JSX.Element {
  const { t } = useTranslation();
  const create = useCreateTherapyType();
  const [name, setName] = useState('');
  const [mins, setMins] = useState('45');
  const onName = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }, []);
  const onMins = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setMins(e.target.value);
  }, []);
  const onSubmit = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      if (name.trim().length < 2) {
        return;
      }
      const parsed = Number(mins);
      const duration = Number.isFinite(parsed) && parsed > 0 ? parsed : 45;
      create.mutate(
        { name: name.trim(), defaultDurationMins: duration },
        {
          onSuccess: () => {
            setName('');
            setMins('45');
          },
        },
      );
    },
    [create, name, mins],
  );
  return (
    <form onSubmit={onSubmit} className="mb-4 flex flex-wrap items-end gap-3">
      <div className="flex-1">
        <label
          htmlFor="tt-name"
          className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300"
        >
          {t('settings.therapyTypes.name')}
        </label>
        <input id="tt-name" className={`${INPUT} w-full`} value={name} onChange={onName} />
      </div>
      <div className="w-28">
        <label
          htmlFor="tt-mins"
          className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300"
        >
          {t('settings.therapyTypes.duration')}
        </label>
        <input
          id="tt-mins"
          type="number"
          min={5}
          max={480}
          className={`${INPUT} w-full`}
          value={mins}
          onChange={onMins}
        />
      </div>
      <Button type="submit" disabled={create.isPending}>
        <Plus className="h-4 w-4" aria-hidden="true" />
        {t('settings.therapyTypes.add')}
      </Button>
    </form>
  );
}

/** Settings → Therapy Types CRUD (Figma). */
export function TherapyTypesPanel(): JSX.Element {
  const { t } = useTranslation();
  const types = useTherapyTypes(false);
  const rows = types.data ?? [];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
      <h3 className="mb-1 font-semibold text-slate-800 dark:text-white">
        {t('settings.therapyTypes.title')}
      </h3>
      <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
        {t('settings.therapyTypes.subtitle')}
      </p>
      <AddTypeForm />
      <QueryState
        isLoading={types.isLoading}
        isError={types.isError}
        isEmpty={rows.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('settings.therapyTypes.empty')}
      >
        <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
          <table className="w-full text-left text-sm">
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((item) => (
                <TypeRow key={item.id} item={item} />
              ))}
            </tbody>
          </table>
        </div>
      </QueryState>
    </div>
  );
}
